import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, CartDrawerPromotionsLoadingEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} promotionsLoadingOverlay - Loading overlay for promotions and discounts.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartDrawerPromotionsLoading, this.#handlePromotionsLoading);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener('click', this.#handleInstallmentsClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartDrawerPromotionsLoading, this.#handlePromotionsLoading);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
  }

  #handleCartAdd = () => {
    // The dedicated add-to-cart confirmation flow replaces drawer auto-open behavior.
    if (document.querySelector('atc-confirmation[data-auto-open="true"]')) return;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();
  }

  /**
   * Close cart drawer when Afterpay installments CTA is clicked to avoid overlapping dialogs.
   * Afterpay injects <square-placement> as a sibling after .cart__installments.
   *
   * @param {Event} event
   */
  #handleInstallmentsClick = (event) => {
    const squarePlacement = event.composedPath().find(
      (node) => node instanceof HTMLElement && node.tagName === 'SQUARE-PLACEMENT'
    );

    if (!(squarePlacement instanceof HTMLElement)) return;
    if (!this.contains(squarePlacement)) return;
    if (squarePlacement.getAttribute('data-page-type') !== 'mini-cart') return;

    this.closeDialog();
  };

  close() {
    this.closeDialog();
  }

  /**
   * Shows or hides the promotions/discounts loading overlay.
   * @param {boolean} isLoading
   */
  setPromotionsLoading(isLoading) {
    const overlay = this.refs.promotionsLoadingOverlay;
    const { dialog } = /** @type {Refs} */ (this.refs);

    if (!(overlay instanceof HTMLElement)) return;

    overlay.classList.toggle('hidden', !isLoading);
    overlay.setAttribute('aria-hidden', String(!isLoading));

    if (dialog instanceof HTMLDialogElement) {
      if (isLoading) {
        dialog.setAttribute('aria-busy', 'true');
      } else {
        dialog.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * @param {Event} event
   */
  #handlePromotionsLoading = (event) => {
    if (!(event instanceof CartDrawerPromotionsLoadingEvent)) return;

    const target = event.target;
    if (!(target instanceof Node) || !this.contains(target)) return;

    this.setPromotionsLoading(Boolean(event.detail.loading));
  };

  updateSummaryStickyState() {
    this.#updateStickyState();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
